import supabase from './supabaseUtils';
import { Resend } from 'resend';

// Initialize Resend with your API key
const resendApiKey = import.meta.env.VITE_RESEND_API_KEY || import.meta.env.RESEND_API_KEY;
const resend = resendApiKey ? new Resend(resendApiKey) : null;
const fromEmail = import.meta.env.VITE_FROM_EMAIL || import.meta.env.RESEND_FROM_EMAIL || 'notifications@playmakerpics.com';
const fromName = import.meta.env.VITE_FROM_NAME || import.meta.env.RESEND_FROM_NAME || 'Playmaker Pics';

/**
 * Sends an order confirmation email
 * @param {Object} orderData - The order data
 * @param {string} email - The recipient's email address
 * @returns {Promise<Object>} - The result of the email sending operation
 */
export const sendOrderConfirmationEmail = async (orderData, email) => {
  try {
    // First, try to fetch export_url for any purchased items
    let enrichedItems = [];
    try {
      if (orderData.items && orderData.items.length > 0) {
        const itemIds = orderData.items.map(item => item.id).filter(Boolean);
        
        if (itemIds.length > 0) {
          const { data: exportItems } = await supabase
            .from('exports')
            .select('id, name, export_url')
            .in('id', itemIds)
            .eq('status', 'active');
            
          if (exportItems && exportItems.length > 0) {
            // Create a map for quick lookup
            const exportMap = {};
            exportItems.forEach(item => {
              exportMap[item.id] = item;
            });
            
            // Enrich items with export URLs
            enrichedItems = orderData.items.map(item => {
              const exportItem = exportMap[item.id];
              return {
                name: item.name || exportItem?.name || 'Custom Design',
                id: item.id,
                download_url: exportItem?.export_url || null
              };
            });
          }
        }
      }
    } catch (error) {
      console.warn('Error fetching export data for email:', error);
      // Fallback to original items
      enrichedItems = orderData.items?.map(item => ({
        name: item.name || 'Custom Design',
        id: item.id
      })) || [];
    }
    
    // Format the order data for the email
    const emailData = {
      email,
      orderId: orderData.orderNumber || orderData.id,
      orderData: {
        id: orderData.orderNumber || orderData.id,
        amount: parseFloat(orderData.total || orderData.amount || 0).toFixed(2),
        date: orderData.orderDate || new Date().toISOString(),
        package: orderData.package?.name || 'Standard',
        itemCount: orderData.items?.length || 0,
        items: enrichedItems
      },
      type: 'order_confirmation'
    };
    
    console.log('Sending order confirmation email with data:', {
      orderId: emailData.orderId,
      amount: emailData.orderData.amount,
      items: emailData.orderData.items.length,
      hasDownloadUrls: emailData.orderData.items.some(item => item.download_url)
    });
    
    // Call the Resend edge function
    const { error } = await supabase.functions.invoke('send-order-confirmation', {
      body: emailData
    });
    
    if (error) throw new Error(error.message || 'Failed to send email');
    
    // Log the email event
    await logEmailSent('order_confirmation', email, emailData.orderId);
    
    return {
      success: true,
      message: 'Order confirmation email sent successfully'
    };
  } catch (error) {
    console.error('Error sending confirmation email:', error);
    throw error;
  }
};

/**
 * Sends download links to the user's email
 * @param {Array} downloadLinks - Array of download links
 * @param {string} email - The recipient's email address
 * @param {string} orderNumber - The order number
 * @returns {Promise<Object>} - The result of the email sending operation
 */
export const sendDownloadLinksEmail = async (downloadLinks, email, orderNumber) => {
  try {
    console.log(`Sending download links for order ${orderNumber} to ${email}`);
    
    // Format the links for the email
    const linkItems = downloadLinks.map(link => ({
      name: link.name || 'Digital Image',
      url: link.url
    }));
    
    // Call the Resend edge function
    const { error } = await supabase.functions.invoke('send-order-confirmation', {
      body: {
        email,
        orderId: orderNumber,
        links: linkItems,
        type: 'download_links'
      }
    });
    
    if (error) throw new Error(error.message || 'Failed to send email');
    
    // Log the email event
    await logEmailSent('download_links', email, orderNumber);
    
    return {
      success: true,
      message: 'Download links email sent successfully'
    };
  } catch (error) {
    console.error('Error sending download links email:', error);
    throw error;
  }
};

/**
 * Log email event (for internal tracking)
 * @param {string} type - Email type 
 * @param {string} email - Recipient email
 * @param {string} orderId - Order ID
 * @returns {Promise}
 */
export const logEmailSent = async (type, email, orderId) => {
  try {
    const { error } = await supabase.from('email_tracking').insert({
      email_type: type,
      recipient_email: email,
      related_id: orderId,
      sent_at: new Date().toISOString(),
      status: 'sent'
    });
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error logging email event:', error);
    return { success: false, error };
  }
};

/**
 * Sends a magic link email using Supabase Functions to proxy to Resend
 * @param {string} email - The recipient's email
 * @param {string} magicLink - The magic link URL
 * @returns {Promise<object>} - The result of the email sending operation
 */
export const sendMagicLinkEmail = async (email, magicLink) => {
  try {
    // Instead of calling Resend directly (which causes CORS issues),
    // use Supabase Functions as a backend proxy
    console.log('Sending magic link email via Supabase Function');
    
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        to: email,
        subject: 'Your PlayMaker Pics Login Link',
        fromName: fromName,
        fromEmail: fromEmail,
        emailType: 'magic-link',
        data: {
          magicLink,
          email
        }
      }
    });

    if (error) {
      console.error('Error sending email via Supabase Function:', error);
      return { error };
    }

    console.log('Magic link email sent successfully via Supabase Function:', data);
    return { data };
  } catch (error) {
    console.error('Exception sending email via Supabase Function:', error);
    return { error };
  }
};

/**
 * Generates a magic link URL with Supabase token
 * @param {string} email - The recipient's email
 * @param {string} redirectTo - The URL to redirect to after authentication
 * @returns {Promise<string>} - The magic link URL
 */
export const generateMagicLinkUrl = async (email, redirectTo) => {
  // This function implementation will depend on how you want to generate
  // your magic links - using Supabase directly or custom token generation
  // You can implement this later based on your specific requirements
  return null;
};

/**
 * Sends a welcome email to a new user using Supabase Functions
 * @param {string} email - The recipient's email
 * @param {string} firstName - The user's first name
 * @returns {Promise<object>} - The result of the email sending operation
 */
export const sendWelcomeEmail = async (email, firstName) => {
  try {
    console.log('Sending welcome email via Supabase Function');
    
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        to: email,
        subject: 'Welcome to PlayMaker Pics!',
        fromName: fromName,
        fromEmail: fromEmail,
        emailType: 'welcome',
        data: {
          firstName,
          dashboardUrl: `${window.location.origin}/dashboard`
        }
      }
    });

    if (error) {
      console.error('Error sending welcome email via Supabase Function:', error);
      return { error };
    }

    console.log('Welcome email sent successfully via Supabase Function:', data);
    return { data };
  } catch (error) {
    console.error('Exception sending welcome email via Supabase Function:', error);
    return { error };
  }
}; 